import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const request = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
}

export const fleetMaintenanceSchedulePublishV1Schema = z.object({
  ...request,
  scheduleId: entityIdSchema,
  scheduleVersionId: entityIdSchema,
  assetId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  expectedVersion: z.literal(0),
  name: z.string().trim().min(3).max(160),
  dueOn: z.iso.date().nullable(),
  dueMeterMilli: z.number().int().nonnegative().nullable(),
  baselineOn: z.iso.date().nullable(),
  calendarIntervalDays: z.number().int().positive().max(3_650).nullable(),
  warningLeadDays: z.number().int().nonnegative().max(365),
  warningLeadMeterMilli: z.number().int().nonnegative(),
}).strict().superRefine((value, context) => {
  if (
    value.dueOn === null
    && value.dueMeterMilli === null
    && (value.baselineOn === null || value.calendarIntervalDays === null)
  ) {
    context.addIssue({
      code: "custom",
      message: "Maintenance requires a date, meter, or calendar rule.",
    })
  }
})

export const fleetMaintenanceEvaluateV1Schema = z.object({
  ...request,
  evaluationId: entityIdSchema,
  workItemId: entityIdSchema,
  warningId: entityIdSchema,
  assetId: entityIdSchema,
  scheduleVersionId: entityIdSchema,
  assetRevisionId: entityIdSchema,
  statusEventId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  expectedVersion: z.number().int().positive(),
  evaluatedAt: z.iso.datetime({ offset: true }),
}).strict()

export const fleetMaintenanceResultV1Schema = z.object({
  entityType: z.literal("fleet_asset"),
  entityId: entityIdSchema,
  state: z.enum(["current", "warning", "due", "overdue"]),
  version: z.number().int().positive(),
  scheduleVersionId: entityIdSchema,
  evaluationId: entityIdSchema,
  workItemId: entityIdSchema.nullable(),
  warningId: entityIdSchema.nullable(),
  maintenanceStatus: z.enum(["current", "warning", "due", "overdue"]),
  operatingStatus: z.enum(["Available", "Maintenance Due", "Out of Service"]),
}).strict()

export type FleetMaintenanceEvaluateV1 =
  z.infer<typeof fleetMaintenanceEvaluateV1Schema>
export type FleetMaintenanceResultV1 =
  z.infer<typeof fleetMaintenanceResultV1Schema>
export type FleetMaintenanceSchedulePublishV1 =
  z.infer<typeof fleetMaintenanceSchedulePublishV1Schema>
